/**
 * Catálogo de los documentos PDF que emite Tumin y de las opciones de
 * apariencia que cada institución puede personalizar (papel, tipografía,
 * logotipo, marca de agua) y de las secciones opcionales de cada documento.
 *
 * Es la única fuente de verdad: el esquema de configuración, el paquete de
 * PDF y la pantalla de administración leen de aquí.
 */
import { keyedEnum } from '../catalogs/enum.js';
// ── Documentos ──────────────────────────────────────────────────────────────
/** Documentos que la institución puede personalizar. */
export const DOCUMENT_KINDS = [
    'loanContract',
    'paymentPlan',
    'paymentReceipt',
    'accountStatement',
    'paidOffCertificate',
    'disbursementVoucher',
    'cashClosing',
    'tabularReport',
];
/** Catálogo con clave de documentos: `DOCUMENT_KIND.loanContract`. */
export const DOCUMENT_KIND = keyedEnum(DOCUMENT_KINDS);
/** Título por defecto de cada documento (también es su nombre en la administración). */
export const DOCUMENT_KIND_LABELS = {
    loanContract: 'Contrato de préstamo',
    paymentPlan: 'Plan de pagos',
    paymentReceipt: 'Recibo de pago',
    accountStatement: 'Estado de cuenta',
    paidOffCertificate: 'Constancia de cancelación',
    disbursementVoucher: 'Constancia de desembolso',
    cashClosing: 'Cierre de caja',
    tabularReport: 'Reportes',
};
/** Descripción breve de cada documento para la pantalla de administración. */
export const DOCUMENT_KIND_DESCRIPTIONS = {
    loanContract: 'Contrato con hoja de información al usuario, pagaré y plan de pagos como anexo.',
    paymentPlan: 'Tabla de cuotas que se entrega al cliente antes o después del desembolso.',
    paymentReceipt: 'Comprobante de cada pago, en media carta.',
    accountStatement: 'Posición del préstamo, cuotas e historial de pagos a una fecha.',
    paidOffCertificate: 'Solvencia que certifica que el préstamo se pagó por completo.',
    disbursementVoucher: 'Constancia de recepción del dinero firmada por el cliente.',
    cashClosing: 'Arqueo diario de la caja con movimientos y recibos.',
    tabularReport: 'Reportes impresos desde el módulo de reportes.',
};
/** Capacidades de personalización de cada documento. */
export const DOCUMENT_KIND_CAPABILITIES = {
    loanContract: { customTitle: true, extraClauses: true },
    paymentPlan: { customTitle: true, extraClauses: false },
    paymentReceipt: { customTitle: true, extraClauses: false },
    accountStatement: { customTitle: true, extraClauses: false },
    paidOffCertificate: { customTitle: true, extraClauses: false },
    disbursementVoucher: { customTitle: true, extraClauses: false },
    cashClosing: { customTitle: true, extraClauses: false },
    tabularReport: { customTitle: false, extraClauses: false },
};
// ── Secciones opcionales ────────────────────────────────────────────────────
/**
 * Secciones que la institución puede mostrar u ocultar en cada documento.
 * Solo se listan partes informativas: el texto legal obligatorio (revelación
 * de costos, cláusulas del contrato, requisitos del pagaré) NO es opcional.
 */
export const DOCUMENT_SECTIONS = {
    loanContract: ['promissoryNote', 'fingerprintBoxes'],
    paymentPlan: ['clientData', 'summary', 'taxColumn', 'balanceColumn'],
    paymentReceipt: ['allocationDetail', 'outstandingPrincipal', 'nextDueDate', 'cashierSignature'],
    accountStatement: ['loanData', 'installmentDetail', 'paymentHistory'],
    paidOffCertificate: ['summary'],
    disbursementVoucher: ['loanData', 'fingerprintBoxes'],
    cashClosing: ['movements', 'receipts', 'supervisorSignature'],
    tabularReport: ['filters'],
};
/** Etiqueta en español de cada sección opcional. */
export const DOCUMENT_SECTION_LABELS = {
    loanContract: {
        promissoryNote: 'Pagaré (título ejecutivo que acompaña al contrato)',
        fingerprintBoxes: 'Recuadros para huella dactilar',
    },
    paymentPlan: {
        clientData: 'Datos del cliente',
        summary: 'Resumen de totales',
        taxColumn: 'Columna de IVA',
        balanceColumn: 'Columna de saldo',
    },
    paymentReceipt: {
        allocationDetail: 'Detalle de aplicación del pago por cuota',
        outstandingPrincipal: 'Saldo de capital después del pago',
        nextDueDate: 'Fecha del próximo pago',
        cashierSignature: 'Firma de quien recibe',
    },
    accountStatement: {
        loanData: 'Datos del préstamo',
        installmentDetail: 'Detalle de cuotas',
        paymentHistory: 'Historial de pagos',
    },
    paidOffCertificate: { summary: 'Recuadro de resumen (monto, último pago, total pagado)' },
    disbursementVoucher: {
        loanData: 'Datos del préstamo',
        fingerprintBoxes: 'Recuadro para huella dactilar',
    },
    cashClosing: {
        movements: 'Movimientos de caja',
        receipts: 'Recibos emitidos',
        supervisorSignature: 'Firma del supervisor',
    },
    tabularReport: { filters: 'Filtros aplicados' },
};
/** Lista de claves de las secciones opcionales de un documento. */
export function documentSectionKeys(kind) {
    return DOCUMENT_SECTIONS[kind];
}
// ── Apariencia ──────────────────────────────────────────────────────────────
/**
 * Tamaños de papel. En Guatemala se usan carta y oficio (8.5 × 13 in); el
 * oficio estadounidense (legal, 8.5 × 14 in) también se consigue.
 */
export const PAPER_SIZES = ['letter', 'oficio', 'legal'];
/** Catálogo con clave de tamaños de papel. */
export const PAPER_SIZE = keyedEnum(PAPER_SIZES);
/** Nombre en español de cada tamaño de papel. */
export const PAPER_SIZE_LABELS = {
    letter: 'Carta (8.5 × 11 pulg.)',
    oficio: 'Oficio (8.5 × 13 pulg.)',
    legal: 'Legal (8.5 × 14 pulg.)',
};
/** Escalas tipográficas de los documentos. */
export const FONT_SCALES = ['compact', 'normal', 'large'];
/** Catálogo con clave de escalas tipográficas. */
export const FONT_SCALE = keyedEnum(FONT_SCALES);
/** Nombre en español de cada escala tipográfica. */
export const FONT_SCALE_LABELS = {
    compact: 'Compacta',
    normal: 'Normal',
    large: 'Grande (lectura fácil)',
};
/** Posiciones del logotipo en el encabezado. */
export const LOGO_POSITIONS = ['left', 'center', 'right'];
/** Catálogo con clave de posiciones del logotipo. */
export const LOGO_POSITION = keyedEnum(LOGO_POSITIONS);
/** Nombre en español de cada posición del logotipo. */
export const LOGO_POSITION_LABELS = {
    left: 'A la izquierda del nombre',
    center: 'Centrado sobre el encabezado',
    right: 'A la derecha, junto al título',
};
/** A qué ejemplares se aplica la marca de agua personalizada. */
export const WATERMARK_SCOPES = ['copies', 'all'];
/** Catálogo con clave de alcances de la marca de agua. */
export const WATERMARK_SCOPE = keyedEnum(WATERMARK_SCOPES);
/** Nombre en español de cada alcance de la marca de agua. */
export const WATERMARK_SCOPE_LABELS = {
    copies: 'Solo en las copias (no en el original)',
    all: 'En todos los ejemplares',
};
//# sourceMappingURL=catalog.js.map